import { PayloadAction } from '@reduxjs/toolkit';

import { displayConfig } from '../configs/displayConfig';
import { HOME_COMMON_PARTNERS_CHANNELS, PRODUCTS, SESSION_KEYS } from '../lib/constants/appConstants';
import {
  additionaPackagesItemAPI,
  DisplayConfig,
  ExtraBuildingsAPI,
  Offer,
  OfferResponseAPI,
  OfferSelectionsAPI,
  OfferState,
  Package,
  packageItemAPI,
  PricingOptionsAPI,
  PricingQuestion,
  PricingQuestionsAPI,
  PricingQuestionsResponseAPI,
  PricingStepConfig,
  RulesConfig,
  SelectionsAPI,
  ValueOptions
} from '../interfaces/Types';
import { AppConstants } from './AppConstants';
import { InsuranceHelper } from './InsuranceHelper';
import { API_OPERATIONS, CHANNELS } from '../lib/constants/appConstants';

export class Mapper {
  static pricingConfig: PricingStepConfig[] = [];
  static insuranceConfig: Package[] = [];

  static setPricingConfig = (pricingConfig: PricingStepConfig[]) =>
    (Mapper.pricingConfig = pricingConfig);
  static setinsuranceConfig = (insuranceConfig: Package[]) =>
    (Mapper.insuranceConfig = insuranceConfig);

  static isPackageEligibleToSelect = (
    isEnabled: boolean,
    packageItem: Package,
    packageList: Package[],
    productName: string
  ) => {
    if (packageItem.isPlusPackage) {
      const selectedPackage = packageList.find(
        (item: Package) => item.isSelected
      );
      const isExcludedWithSelectedPackage =
        selectedPackage &&
        Mapper.retrieveExcludedItems(
          selectedPackage,
          productName,
          'excludedAdditionalPackages'
        )?.includes(packageItem.packageCode);
      return (
        isEnabled &&
        !isExcludedWithSelectedPackage &&
        !!selectedPackage?.isEnabled
      );
    }
    return isEnabled;
  };

  static mapTemplateToQuestions = (
    templateResponse: PricingQuestionsResponseAPI,
    offerInfo: Offer
  ): PricingStepConfig[] => {
    const templateConfig: PricingStepConfig[] = Mapper.pricingConfig;
    if (templateConfig.length > 0) {
      templateConfig.forEach((questionsInfo: PricingStepConfig) => {
        questionsInfo.questions.forEach((questions: PricingQuestion) => {
          Mapper.buildQuestionsState(questions, templateResponse, offerInfo);
        });
        if (questionsInfo.additionalQuestions) {
          questionsInfo.additionalQuestions.forEach(
            (questions: PricingQuestion) => {
              Mapper.buildQuestionsState(
                questions,
                templateResponse,
                offerInfo
              );
            }
          );
        }
      });
    }
    return templateConfig;
  };

  static buildQuestionsState = (
    questions: PricingQuestion,
    templateResponse: PricingQuestionsResponseAPI,
    offerInfo: Offer
  ) => {
    if (questions?.APIKey) {
      const templateDetails = templateResponse.types.find(
        (responseItem: PricingQuestionsAPI) => {
          return responseItem?.keyType === questions.APIKey;
        }
      );
      const questionType = questions.APIKey || '';
      if (templateDetails && questions?.options) {
        const clonedOptions = [...(questions.options || [])];
        templateDetails.options.forEach((apiOptions: PricingOptionsAPI) => {
          if (questions.options !== undefined) {
            clonedOptions.push({
              name: apiOptions.key,
              label: apiOptions.key,
              value: apiOptions.value,
              disabled: InsuranceHelper.isDisabled(
                questionType,
                apiOptions.value,
                offerInfo.productName
              ),
              isDefault: InsuranceHelper.getDefault(
                questionType,
                apiOptions.value,
                apiOptions.default,
                offerInfo.productName
              )
            });
          }
        });
        // Create a new object to avoid modifying the read-only property
        const updatedQuestions = {
          ...questions,
          options: clonedOptions
        };
        // Replace the original questions object with the updated one
        Object.assign(questions, updatedQuestions);
      }
      questions.selectedValue = InsuranceHelper.getDefaultSelectedValue(
        questions,
        questionType,
        offerInfo.productName
      );
      questions.isDisabled = InsuranceHelper.isDisabled(
        questionType,
        questions.selectedValue,
        offerInfo.productName
      );
      questions.isImmutable = InsuranceHelper.isImmutable(
        questionType,
        offerInfo.productName
      );
      questions.isValid = !!questions.selectedValue;
      questions.errorMessage = '';
    }
    return questions;
  };

  static setCustomerInfoInSession = (
    product: string,
    offerRequest: OfferSelectionsAPI,
    offerResponse: OfferResponseAPI
  ) => {
    let customerInfo = {};
    switch (product) {
      case PRODUCTS.CAR:
      case PRODUCTS.MOPED:
        customerInfo = {
          cprNumber: '',
          registrationNumber: Mapper.getSelectedValue(offerRequest, 'regPlate')
        };
        break;
      case PRODUCTS.MC:
        customerInfo = {
          cprNumber: '',
          registrationNumber: Mapper.getSelectedValue(offerRequest, 'regPlate'),
          customerName: offerResponse.customerName,
        };
        break;
      case PRODUCTS.ACCIDENT:
        customerInfo = {
          cprNumber: '',
          customerName: offerResponse.customerName,
        };
        break;
      case PRODUCTS.HOME:
      case PRODUCTS.VILLA:
        customerInfo = {
          cprNumber: '',
          customerName: offerResponse.customerName,
          zipCode: Mapper.getSelectedValue(offerRequest, 'zip'),
          customerAddress: Mapper.getSelectedValue(
            offerRequest,
            'otherAddress'
          ),
          houseType: Mapper.getSelectedValue(offerRequest, 'houseType'),
          channel: offerRequest.channel || 'gjensidige',
          propertyId:
            product === PRODUCTS.VILLA
              ? Mapper.getSelectedValue(offerRequest, 'propertyId')
              : ''
        };
        break;
      case PRODUCTS.PPI:
        customerInfo = {
          cprNumber: offerRequest.cpr,
          customerName: offerResponse.customerName,
          channel: offerRequest.channel || 'gjensidige',
        };
        break;
    }
    sessionStorage.setItem(
      SESSION_KEYS.CUSTOMER_INFO,
      JSON.stringify(customerInfo)
    );
  };

  static shouldResetSelection = (apiOperation: API_OPERATIONS, key: string, channel: string) => {
    return apiOperation === API_OPERATIONS.RESUME_OFFER &&
      key === 'noOfPeople' &&
      HOME_COMMON_PARTNERS_CHANNELS.includes(channel?.toLowerCase());
  }

  static getSelectedValue = (
    offerRequest: OfferSelectionsAPI,
    keyName: string
  ) =>
    offerRequest.attributeList.find(
      (attribute: SelectionsAPI) => attribute.key === keyName
    )?.value || '';

  static mapSelectionsToQuestiions = (
    state: OfferState,
    customerSelections: OfferSelectionsAPI,
    apiOperation?: API_OPERATIONS
  ) => {
    state.pricingQuestions.forEach((questionsInfo: PricingStepConfig) => {
      questionsInfo.questions.forEach((question: PricingQuestion) => {
        if (question?.APIKey && question?.options) {
          const selection = customerSelections.attributeList.find(
            (responseItem: SelectionsAPI) => {
              return responseItem?.key === question.APIKey;
            }
          );
          if (
            apiOperation &&
            question?.APIKey &&
            customerSelections?.channel &&
            Mapper.shouldResetSelection(apiOperation, question?.APIKey, customerSelections?.channel)
          ) {
            question.selectedValue = '';
          } else {
            const defaultOption = question.options?.find(
              (option: ValueOptions) => option.isDefault
            ) || { value: '' };
            question.selectedValue = selection?.value || defaultOption?.value;
          }
          question.isValid = !!question.selectedValue;
        }
        //  enabled alert visibility is offer is saved offer 
        if (state.offer.channel === "hgfPPI" && state.offer.productName === PRODUCTS.PPI && state.offer?.salesChannel === 'M') {
          if (question?.questionKey === 'coverageAmount') {
            question.isVisible = true;
            question.isEnabled = true;
          }
          // In fture if any other question is needed to be enabled then add here
        }
      });
      if (
        customerSelections?.extraBuildingList &&
        customerSelections?.extraBuildingList.length > 0
      ) {
        Mapper.mapExtraBuildingToQuestions(
          state,
          customerSelections?.extraBuildingList,
          questionsInfo
        );
      }
    });
    return state.pricingQuestions;
  };

  static mapExtraBuildingToQuestions = (
    state: OfferState,
    extraBuildingList: ExtraBuildingsAPI[],
    questionsInfo: PricingStepConfig
  ) => {
    const additionalBuildingsList: any = [];
    extraBuildingList.map((building: ExtraBuildingsAPI) => {
      let selectedType = {
        label: '',
        value: ''
      };
      let selectedArea = {
        label: '',
        value: ''
      };
      let selectedEnergyType = {
        label: '',
        value: ''
      };

      if (building.extraBuildingArea) {
        selectedArea = {
          label: Mapper.getAdditionalOptionLabel(
            building.extraBuildingArea,
            'extraBuildingArea',
            questionsInfo.additionalQuestions
          ),
          value: building.extraBuildingArea
        };
      }
      if (building.extraFloors) {
        selectedType = {
          label: Mapper.getAdditionalOptionLabel(
            building.extraFloors,
            'extraFloors',
            questionsInfo.additionalQuestions
          ),
          value: building.extraFloors
        };
      }
      if (building.extraFront) {
        selectedEnergyType = {
          label: Mapper.getAdditionalOptionLabel(
            building.extraFront,
            'extraFront',
            questionsInfo.additionalQuestions
          ),
          value: building.extraFront
        };
      }
      if (
        selectedType.label !== '' &&
        selectedArea.label !== '' &&
        selectedEnergyType.label !== ''
      ) {
        additionalBuildingsList?.push({
          type: selectedType,
          area: selectedArea,
          energyType: selectedEnergyType
        });
      }
    });
    state.additionalInputs = additionalBuildingsList;
  };

  static getAdditionalOptionLabel = (
    selectedValue: string,
    apiKey: string,
    additionalQuestions?: PricingQuestion[]
  ) => {
    let selectedLabel = '';
    if (additionalQuestions) {
      const options =
        additionalQuestions.find(
          (questions: PricingQuestion) => questions.APIKey === apiKey
        )?.options || [];
      if (options.length > 0) {
        selectedLabel =
          options.find((option: ValueOptions) => option.value === selectedValue)
            ?.name || '';
      }
    }
    return selectedLabel;
  };

  static getSelectedPackage = (packages: Package[]) =>
    packages.find((packageItem: Package) => packageItem.isSelected);

  static mapOfferToPackages = (
    state: OfferState,
    offerResponse: OfferResponseAPI
  ) => {
    let packageConfig: Package[] = state.packages.length
      ? state.packages
      : JSON.parse(JSON.stringify(Mapper.insuranceConfig));

    const totalPackageList = [
      ...(offerResponse.packages || []),
      ...(offerResponse.additionalPackages || [])
    ];
    const defaultPackageName =
      offerResponse.packages.find((packageItem: packageItemAPI) => {
        return packageItem.packageType === offerResponse.defaultPackage;
      })?.packageName || '';

    if (packageConfig.length > 0) {
      packageConfig = Mapper.buildInsurancePackageState(
        packageConfig,
        totalPackageList,
        defaultPackageName,
        offerResponse,
        state
      );
    }
    state.offer.disabledPackages = Mapper.getDisabledPackages(totalPackageList);
    return packageConfig;
  };

  static buildInsurancePackageState = (
    packageConfig: Package[],
    totalPackageList: any[],
    defaultPackageName: string,
    offerResponse: OfferResponseAPI,
    state: OfferState
  ) => {

    const validPackages = packageConfig.filter((packageItem: Package) => {
      const responsePackageItem = totalPackageList.find((responseItem: any) => {
        return responseItem.packageName === packageItem.packageName;
      });
      return responsePackageItem !== undefined;
    });

    validPackages.forEach((packageItem: Package) => {
      const responsePackageItem = totalPackageList.find((responseItem: any) => {
        return responseItem.packageName === packageItem.packageName;
      });

      if (responsePackageItem) {
        packageItem.defaultPackage =
          packageItem.packageName === defaultPackageName;
        packageItem.defaultPackageMessage = packageItem.defaultPackage
          ? offerResponse.defaultPackagemessage
          : '';
        packageItem.disabledMessage = responsePackageItem.disabledMessage || '';
        packageItem.plusPackagetype = responsePackageItem.plusPackagetype || '';
        packageItem.monthlyPrice = responsePackageItem.monthlyPrice?.toString();
        packageItem.currency = responsePackageItem.monthlyPrice
          ? responsePackageItem.currency
          : '';
        packageItem.priceFrequency = responsePackageItem.priceFrequency;
        packageItem.isEnabled = Mapper.isPackageEligibleToSelect(
          responsePackageItem.selectable,
          packageItem,
          packageConfig,
          state.offer.productName
        );
        packageItem.isSelected = Mapper.isPackageSelectedByDefault(
          offerResponse,
          packageItem,
          state.offer.productName
        );
        packageItem.priceDescription = responsePackageItem.priceDescription;
        if (packageItem.isSelected && !packageItem.isPlusPackage) {
          InsuranceHelper.setDefaultSelectedPackage(packageItem.packageCode);
        }
        packageItem.deductibleInfo = packageItem.deductibleInfo || '';
        packageItem.secondaryDeductibleInfo =
          packageItem.secondaryDeductibleInfo || '';
        packageItem.totalCoverageInfo = packageItem.totalCoverageInfo || '';
      }
    });
    return validPackages;
  };

  static mapSelectedOffersToPackages = (
    state: OfferState,
    offerResponse: OfferResponseAPI
  ) => {
    const packageConfig: Package[] = Mapper.insuranceConfig;
    const totalPackageList =
      InsuranceHelper.getSelectedPackagesFromAPI(offerResponse);
    if (packageConfig.length > 0) {
      packageConfig.forEach((packageItem: Package) => {
        const responsePackageItem = totalPackageList.find(
          (responseItem: any) => {
            return responseItem.packageName === packageItem.packageName;
          }
        );
        if (responsePackageItem) {
          packageItem.monthlyPrice =
            responsePackageItem.monthlyPrice?.toString();
          packageItem.currency = responsePackageItem.monthlyPrice
            ? responsePackageItem.currency
            : '';
          packageItem.priceFrequency = responsePackageItem.priceFrequency;
          //packageItem.isEnabled = responsePackageItem.selectable;
          packageItem.isEnabled = Mapper.isPackageEligibleToSelect(
            responsePackageItem.selectable,
            packageItem,
            packageConfig,
            state.offer.productName
          );
          //For home get offer the addOn is returning as selected for all housTypes(common issue with multiple revoke call).This is to validate aginst houseype 2.
          packageItem.isSelected = packageItem.isPlusPackage
            ? InsuranceHelper.isAddOnsAvailableForProductType(
              state.offer.productName
            )
            : true;
        }
      });
    }
    return packageConfig;
  };

  static isPackageSelectedByDefault = (
    offerResponse: OfferResponseAPI,
    packageItem: Package,
    productName: string
  ) => {
    if (packageItem.isPlusPackage) {
      const currentPackage = offerResponse.additionalPackages.find(
        (responsePackageItem: additionaPackagesItemAPI) => {
          return responsePackageItem.packageName === packageItem.packageName;
        }
      );
      return (
        InsuranceHelper.shouldEnablePlusPackageByDefault(
          productName,
          packageItem
        ) && !!currentPackage?.selected
      );
    }
    const selectedPackage = offerResponse.packages.find(
      (packageItem: packageItemAPI | additionaPackagesItemAPI) => {
        return packageItem.selected;
      }
    );

    return selectedPackage
      ? selectedPackage.packageName === packageItem.packageName
      : packageItem.defaultPackage;
  };

  static getDisabledPackages = (
    totalPackageList: packageItemAPI[] | additionaPackagesItemAPI[]
  ) => {
    const disabledPackages: string[] = [];
    totalPackageList.forEach(
      (packageItem: packageItemAPI | additionaPackagesItemAPI) => {
        if (!packageItem.selectable) {
          disabledPackages.push(packageItem.packageName);
        }
      }
    );
    return disabledPackages;
  };

  static updateInsuranceSelection = (
    state: OfferState,
    action: PayloadAction<Package>
  ) => {
    state.packages.forEach((packageItem: Package) => {
      if (!packageItem.isPlusPackage) {
        packageItem.isSelected =
          packageItem.packageCode === action.payload.packageCode;
      }
    });
    Mapper.updateDisplayOnPackageSelection(state, action.payload);
  };

  static updateDisplayOnPackageSelection = (
    state: OfferState,
    selectedPackage: Package
  ) => {
    Mapper.updateQuestionsVisibility(
      state.pricingQuestions,
      selectedPackage,
      state.offer.productName
    );
    Mapper.updatePakagesVisibility(
      state.packages,
      selectedPackage,
      state.offer.productName,
      state.offer.disabledPackages
    );
  };

  static updateQuestionsVisibility = (
    pricingQuestions: PricingStepConfig[],
    selectedPackage: Package,
    productName: string
  ) => {
    const excludedQuestions: string[] = Mapper.retrieveExcludedItems(
      selectedPackage,
      productName,
      'excludedQuestions'
    );
    pricingQuestions.forEach((templateQuestions: PricingStepConfig) => {
      templateQuestions.questions.forEach((question: PricingQuestion) => {
        question.isVisible =
          !excludedQuestions.includes(question.code) /* &&
          selectedPackage.isEnabled; */
      });
    });
  };

  static updatePakagesVisibility = (
    packages: Package[],
    selectedPackage: Package,
    productName: string,
    disabledPackages: string[]
  ) => {
    const excludedAdditionalPackages: string[] = Mapper.retrieveExcludedItems(
      selectedPackage,
      productName,
      'excludedAdditionalPackages'
    );
    packages.forEach((packageItem: Package) => {
      const isPackageExcluded = excludedAdditionalPackages.includes(
        packageItem.packageCode
      );
      packageItem.isEnabled =
        !isPackageExcluded &&
        !disabledPackages.includes(packageItem.packageName);
      if (isPackageExcluded) {
        packageItem.isSelected = InsuranceHelper.shouldSelectExcludedPackage(
          productName,
          packageItem
        );
        packageItem.disabledMessage =
          packageItem.disabledMessage ||
          AppConstants.getAppText('webshop.plusPacket.disableMessage');
      }
    });
  };

  static retrieveExcludedItems = (
    selectedPackage: Package,
    productName: string,
    excludedItem: string
  ): string[] => {
    const productRules: RulesConfig[] =
      displayConfig.find(
        (config: DisplayConfig) => config.product === productName
      )?.rules || [];
    const packageRules = productRules.find(
      (rules: RulesConfig) => rules.packageCode === selectedPackage.packageCode
    );
    if (packageRules) {
      const rules = JSON.parse(JSON.stringify(packageRules) || '{}');
      return !InsuranceHelper.isEmptyObject(rules) ? rules[excludedItem] : [];
    }
    return [];
  };

  static updatePlusPacketSelection = (
    state: OfferState,
    action: PayloadAction<Package>
  ) => {
    state.packages.forEach((packageItem: Package) => {
      if (packageItem.isPlusPackage) {
        if (
          InsuranceHelper.shouldUpdateSelectionOnAllPackage(
            state.offer.productName
          )
        ) {
          packageItem.isSelected = !packageItem.isSelected;
        } else {
          packageItem.isSelected =
            packageItem.packageCode === action.payload.packageCode
              ? !packageItem.isSelected
              : packageItem.isSelected;
        }
      }
    });
  };

  static mapUpdatedOfferToPackages = (
    offerResponse: OfferResponseAPI,
    currentState: any
  ) => {
    const totalPackageList = [
      ...(offerResponse?.packages || []),
      ...(offerResponse?.additionalPackages || [])
    ];

    if (currentState.packages.length > 0) {
      currentState.packages.forEach((packageItem: Package) => {
        const responsePackageItem = totalPackageList.find(
          (responseItem: any) => {
            return responseItem.packageName === packageItem.packageName;
          }
        );
        if (responsePackageItem) {
          packageItem.monthlyPrice =
            responsePackageItem.monthlyPrice?.toString();
          packageItem.currency = responsePackageItem.monthlyPrice
            ? responsePackageItem.currency
            : '';
          packageItem.priceFrequency = responsePackageItem.priceFrequency;
          packageItem.isEnabled = Mapper.isPackageEligibleToSelect(
            responsePackageItem.selectable,
            packageItem,
            currentState.packages,
            currentState.offer.productName
          );
          packageItem.disabledMessage =
            responsePackageItem.disabledMessage || '';

          packageItem.isSelected = responsePackageItem.selectable
            ? Mapper.isPackageSelectedByDefault(offerResponse, packageItem, currentState.offer.productName)
            : false;
        }
      });
    }
    currentState.offer.disabledPackages = Mapper.getDisabledPackages(totalPackageList);
    return currentState.packages;
  };
}
